import type { Paths, SetOptional, SetRequired } from 'type-fest';
import { FormApi, ValidationErrors } from 'final-form';
import { FormProps, FormRenderProps } from 'react-final-form';
import { z } from 'zod';

import {
  Audience_Purchase_Time_Window,
  Audience_Status,
  AudienceAgeWithinTargetingAttributeFragment,
  AudienceAggregatedAudienceTargetingAttributeFragment,
  AudienceCommunicationPreferenceTargetingAttributeFragment,
  AudienceEventConditionFragment,
  AudienceEventPurchaseCondition,
  AudienceExternalIdentityVerificationTargetingAttributeFragment,
  AudienceFragment,
  AudienceInAllSegmentsTargetingAttributeFragment,
  AudienceInAnySegmentsTargetingAttributeFragment,
  AudienceNotInAnySegmentsTargetingAttributeFragment,
  AudienceOfferFulfillmentTargetingAttributeFragment,
  AudiencePointBalanceTargetingAttributeFragment,
  AudiencePurchasedAtLeastTargetingAttributeFragment,
  AudiencePurchasedAtMostTargetingAttributeFragment,
  AudienceStoreLocationTargetingAttributeFragment,
  AudienceSurveyResponseTargetingAttributeFragment,
  AudienceTimeSinceLastVisitTargetingAttributeFragment,
  AudienceTimeSinceMembershipAcquisitionTargetingAttributeFragment,
  AudienceWalletSteeringTargetingAttributeFragment,
  Event_Targeting_Condition_Operator,
  GeneralSurveyFragment,
  LocationFragment,
  LocationGroupFragment,
  OfferFragment,
  ProductCategoryFragment,
  ProductGroupFragment,
  Retailer_Network_Operator,
  Retailer_Network_User_Type,
  RetailerNetworkPartialFragment,
  SteeringWalletBasicInfoFragment,
} from '@occc/graphql';
import { PurchaseFormInitialValues } from '@occc-app/routes/Audience/components/Purchases/types';
import { UseGetAudienceSegmentsReturns } from '@occc/feat-segments';

import { AUDIENCE_STATUS_ALL } from './constants';

import { audienceSchema } from './validation';
import { FulfillmentValues } from './components/Fulfillment/types';
import { WalletSteeringItemsValues } from './components/WalletSteering/types';

import { FulfilledLoyaltyEventValues, PurchasesValues } from './components/QualifiedEvents/types';
import { SurveysValues } from './components/Surveys/types';
import {
  fixedMonthSchema,
  fixedWindowSchema,
  timeWindowSchema,
} from './validation/time-period.validation';
import { RequiredFieldsType } from '../../types';

export enum TransactionTimePeriod {
  PreviousMonth = 'PreviousMonth',
  RollingWindow = 'RollingWindow',
  FixedMonth = 'FixedMonth',
  FixedWindow = 'FixedWindow',
  AllTime = 'AllTime',
}

export enum TimePeriod {
  PreviousMonth = 'PreviousMonth',
  RollingWindow = 'RollingWindow',
  FixedMonth = 'FixedMonth',
  FixedWindow = 'FixedWindow',
}

export enum OfferFulfillment {
  Fulfilled = 'Fulfilled',
  NotFulfilled = 'NotFulfilled',
}

export enum ProductInterestsControl {
  Prefer = 'Prefer',
  NotPrefer = 'NotPrefer',
}

export enum ProductInterestsMatch {
  All = 'All',
  Any = 'Any',
}
export type ArrayItem<T> = T extends Array<infer U> ? U : never;

export type FuelAndConvenienceProfile = {
  label: string;
  value: string;
}[];

export type AudienceFormValues = z.infer<typeof audienceSchema>;

export type AudienceFormValuesPaths = Paths<AudienceFormValues>;

// Purchases
export type Purchase =
  | (AudiencePurchasedAtLeastTargetingAttributeFragment & {
      productGroups: ProductGroupFragment[];
      locations?: LocationFragment[];
      locationGroups?: LocationGroupFragment[];
    })
  | (AudiencePurchasedAtMostTargetingAttributeFragment & {
      productGroups: ProductGroupFragment[];
      locations?: LocationFragment[];
      locationGroups?: LocationGroupFragment[];
    });

// Surveys
export type Survey = AudienceSurveyResponseTargetingAttributeFragment & {
  locations: LocationFragment[];
  locationGroups: LocationGroupFragment[];
};

export type FixedMonthValues = z.infer<typeof fixedMonthSchema>;
export type TimeWindowValues = z.infer<typeof timeWindowSchema>;
export type FixedWindowValues = z.infer<typeof fixedWindowSchema>;

export type PeriodOfThePurchase = {
  timePeriodType: TransactionTimePeriod;
  timeWindow: TimeWindowValues;
  fixedMonth: FixedMonthValues;
  fixedWindow: FixedWindowValues;
};

export type PeriodOfThePurchaseForQualifiedEvents = Omit<
  PeriodOfThePurchase,
  'timePeriodType' | 'fixedMonth'
> & {
  timePeriodType: TimePeriod;
};

export type FulfillmentTargetOffer =
  | {
      id: string;
      name: string;
    }
  | null
  | undefined;

export type FulfillmentFormValues = ArrayItem<FulfillmentValues>;
export type WalletSteeringFormValues = ArrayItem<WalletSteeringItemsValues>;

export enum QualifiedEventType {
  MadePurchase = 'MADE_PURCHASE',
  FulfilledLoyaltyEvent = 'FULFILLED_LOYALTY_EVENT',
}
export type QualifiedEventsBaseFormValues = PeriodOfThePurchaseForQualifiedEvents & {
  times: number;
  timesOperator: Event_Targeting_Condition_Operator;
};

export type MadePurchaseQualifiedEventsFormValues = ArrayItem<PurchasesValues> &
  QualifiedEventsBaseFormValues;

export type FulfilledLoyaltyEventQualifiedEventsFormValues =
  ArrayItem<FulfilledLoyaltyEventValues> & QualifiedEventsBaseFormValues;

export type QualifiedEventsFormValues =
  | MadePurchaseQualifiedEventsFormValues
  | FulfilledLoyaltyEventQualifiedEventsFormValues;

export type MadePurchaseQualifiedEventsInitialFormValues = SetOptional<
  MadePurchaseQualifiedEventsFormValues,
  'times' | 'threshold' | 'timeWindow' | 'fixedWindow'
>;

export type FulfilledLoyaltyEventQualifiedEventsInitialFormValues = SetRequired<
  Partial<FulfilledLoyaltyEventQualifiedEventsFormValues>,
  'event' | 'timePeriodType' | 'timesOperator'
>;

export type QualifiedEventsInitialFormValues =
  | MadePurchaseQualifiedEventsInitialFormValues
  | FulfilledLoyaltyEventQualifiedEventsInitialFormValues;

export type Fulfillment = AudienceOfferFulfillmentTargetingAttributeFragment & {
  targetOffer: FulfillmentTargetOffer;
};

export type HomeStore = AudienceStoreLocationTargetingAttributeFragment & {
  locations?: LocationFragment[];
  locationGroups?: LocationGroupFragment[];
};

export type WalletSteeringAttribute = AudienceWalletSteeringTargetingAttributeFragment & {
  wallet?: SteeringWalletBasicInfoFragment;
};

export type SurveyAttribute = AudienceSurveyResponseTargetingAttributeFragment & {
  survey?: GeneralSurveyFragment;
  locations: LocationFragment[];
  locationGroups: LocationGroupFragment[];
};

export type CombinedAudiencesAttribute = {
  operator: AudienceAggregatedAudienceTargetingAttributeFragment['combinedAudiencesOperator'];
  target: AudienceAggregatedAudienceTargetingAttributeFragment['combinedAudiencesTarget'];
  audiences: Pick<AudienceFragment, 'uuid' | 'name'>[];
};

export type ProductInterestsAttribute = {
  prefer: ProductInterestsControl;
  match: ProductInterestsMatch;
  productCategories?: ProductCategoryFragment[];
};

export type QualifiedEventAttribute = Omit<
  AudienceEventConditionFragment,
  '__typename' | 'purchaseCondition'
> & {
  purchaseCondition?: Omit<AudienceEventPurchaseCondition, '__typename'> & {
    locations: LocationFragment[];
    locationGroups: LocationGroupFragment[];
    productGroups: ProductGroupFragment[];
  };
  loyaltyEvent?: OfferFragment;
};

// Segments
export type Segment =
  | (AudienceInAllSegmentsTargetingAttributeFragment & {
      segments: UseGetAudienceSegmentsReturns['segments'];
    })
  | (AudienceInAnySegmentsTargetingAttributeFragment & {
      segments: UseGetAudienceSegmentsReturns['segments'];
    })
  | (AudienceNotInAnySegmentsTargetingAttributeFragment & {
      segments: UseGetAudienceSegmentsReturns['segments'];
    });

export type EmployeeTargetingAttributes = {
  retailerNetworkUserType: Retailer_Network_User_Type;
  operator?: Retailer_Network_Operator | null;
  retailerNetworks?: RetailerNetworkPartialFragment[];
};

export type TargetingAttributes = {
  ageWithin: AudienceAgeWithinTargetingAttributeFragment | null;
  fuelAndConvenienceProfile: FuelAndConvenienceProfile;
  purchases: Purchase[];
  segments: Segment[];
  fulfillments: Fulfillment[];
  homeStore: HomeStore | null;
  walletSteeringItems: WalletSteeringAttribute[];
  productInterests: ProductInterestsAttribute | null;
  qualifiedEvents: QualifiedEventAttribute[];
  lastVisit: AudienceTimeSinceLastVisitTargetingAttributeFragment;
  pointBalance: AudiencePointBalanceTargetingAttributeFragment | undefined;
  membershipCreated: AudienceTimeSinceMembershipAcquisitionTargetingAttributeFragment | undefined;
  employeeTargetingAttributes?: EmployeeTargetingAttributes | null;
  surveyItems: SurveyAttribute[];
  combinedAudiences: CombinedAudiencesAttribute | null;
  activeSubBrandUuids?: string[];
  externalIdentityVerification?: AudienceExternalIdentityVerificationTargetingAttributeFragment[];
  communicationPreference?: AudienceCommunicationPreferenceTargetingAttributeFragment[];
};

type VerificationAttribute = {
  handle: string;
  name: string;
};

export type ExternalIdentityVerificationFormValue = {
  inAll: {
    verificationAttributes: VerificationAttribute[];
  };
  inAny: {
    verificationAttributes: VerificationAttribute[];
  };
  notInAll: {
    verificationAttributes: VerificationAttribute[];
  };
};

export type TargetingAttributesFormValues = Omit<AudienceFormValues, 'name' | 'description'> & {
  externalIdentityVerification?: ExternalIdentityVerificationFormValue | null;
};
export type SurveysFormValues = ArrayItem<SurveysValues>;

export type AudienceFormInitialValues = {
  purchases: PurchaseFormInitialValues[];
  qualifiedEvents: Partial<QualifiedEventsFormValues>[];
} & Omit<AudienceFormValues, 'purchases' | 'qualifiedEvents'>;

export type AudienceFormProps = FormProps<AudienceFormValues, AudienceFormInitialValues>;
export type AudienceFormRenderProps = FormRenderProps<
  AudienceFormValues,
  AudienceFormInitialValues
>;

export interface CreateAudiencePageProps {
  audienceCreate: AudienceFormProps['onSubmit'];
  required?: RequiredFieldsType<Partial<AudienceFormValues>>;
  validate?: (values: AudienceFormValues) => Promise<ValidationErrors>;
  initialValues: AudienceFormInitialValues;
  openAndScrollToInvalidSection: (
    form: FormApi<AudienceFormValues, Partial<AudienceFormValues>>,
  ) => void;
}

export interface UpdateAudiencePageProps {
  audienceUpdate: AudienceFormProps['onSubmit'];
  required?: RequiredFieldsType<Partial<AudienceFormValues>>;
  validate?: (values: AudienceFormValues) => Promise<ValidationErrors>;
  initialValues: AudienceFormInitialValues;
  openAndScrollToInvalidSection: (
    form: FormApi<AudienceFormValues, Partial<AudienceFormValues>>,
  ) => void;
}

export type Audience = Omit<AudienceFragment, 'targetingAttributes'> & {
  targetingAttributes: TargetingAttributes;
};

export interface AudienceFilterFormValues {
  status: StatusType;
}

export type StatusType = Audience_Status | typeof AUDIENCE_STATUS_ALL | undefined;

export type PeriodValues = {
  relativeMonthsBack?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  targetMonth?: number | null;
  targetYear?: number | null;
  timeWindow?: Audience_Purchase_Time_Window | null;
};
